import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import UndoIcon from "@mui/icons-material/Undo";
import { IconButtonProps, InputBaseComponentProps, OutlinedInputProps } from "@mui/material";
import { green, lightBlue } from "@mui/material/colors";
import IconButton from "@mui/material/IconButton";
import Input from "@mui/material/Input";
import { styled } from "@mui/material/styles";
import TextField, { OutlinedTextFieldProps } from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { DatePicker, DatePickerProps, PickersDay, PickersDayProps, PickersInputProps } from "@mui/x-date-pickers";
import { PickerValue } from "@mui/x-date-pickers/internals";
import AHDate from "_shared/dates/date";
import { diffLines, diffWords } from "diff";
import { enqueueSnackbar } from "notistack";
import React, { forwardRef, useCallback } from "react";
import CommentsButton from "work/proposals/comments/_view/comments-button";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import { ProposalIssue } from "work/proposals/issues/proposal-issues";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChange, { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import { ChangeType } from "work/proposals/redlining/__diff/view/diff-changes";
import DiffText from "work/proposals/redlining/__diff/view/diff-text";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";

const DiffTextContainer = styled("div")(({ theme }) => ({
  flexDirection: "column",
  height: "100%",
  paddingRight: theme.spacing(2),
  width: "100%",
}));
const DiffInputContainer = styled("div")(() => ({
  width: "100%",
  "&.Mui-disabled": {
    webkitTextFillColor: "revert !important",
    opacity: "0.75 !important",
  },
}));
const DiffInputSpan = styled("span")(() => ({
  "&.MuiInputBase-input": {
    flexDirection: "column",
  },
}));
const RedlineDatePicker = styled(DatePicker, {
  shouldForwardProp: (props) => props !== "traversable",
})<{ traversable?: boolean }>(() => ({
  minWidth: "20rem",
  width: "min-content",
}));
const CalendarDay = styled(PickersDay, {
  shouldForwardProp: (prop) => !["resolved", "changeType"].includes(prop.toString()),
})<{ changeType?: ChangeType; resolved: boolean }>(({ changeType, resolved, theme }) => ({
  "&.MuiPickersDay-root": {
    "&:disabled": {
      color: (function (): string {
        if (changeType === ChangeType.added || changeType === ChangeType.removed) {
          return theme.palette.text.primary;
        }
        return "revert";
      })(),
    },
    backgroundColor: (function (): string {
      if (changeType === ChangeType.added && resolved) return lightBlue[700];
      if (changeType === ChangeType.added) return green[300];
      return "unset";
    })(),
    border: (function (): string {
      if (changeType === ChangeType.removed) return `2px solid ${theme.palette.error.main}`;
      return "unset";
    })(),
    textDecoration: (function (): string {
      if (changeType === ChangeType.removed) return "line-through";
      return "unset";
    })(),
    textDecorationColor: (function (): string {
      if (changeType === ChangeType.removed) return theme.palette.error.main;
      return "unset";
    })(),
    textDecorationThickness: (function (): string {
      if (changeType === ChangeType.removed) return "2px";
      return "unset";
    })(),
  },
}));
const ActionButton = styled(IconButton)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(0.5),
}));

interface RedlinePickersDayProps extends PickersDayProps {
  originalDate: AHDate | null;
  revisedDate: AHDate | null;
  currentDate: AHDate | null | undefined;
  resolved: boolean;
}

function RevisedDay(props: Readonly<RedlinePickersDayProps>) {
  const { originalDate, revisedDate, currentDate, resolved, ...otherProps } = props;

  let changeType = ChangeType.unchanged;

  if (!props.outsideCurrentMonth) {
    if (originalDate?.isSame(AHDate.fromMoment(props.day), "day")) {
      changeType = ChangeType.removed;
    }
    if (
      revisedDate?.isSame(AHDate.fromMoment(props.day), "day") ||
      (currentDate?.isSame(AHDate.fromMoment(props.day), "day") && Boolean(resolved))
    ) {
      changeType = ChangeType.added;
    }
  }

  return (
    <CalendarDay
      {...otherProps}
      disabled={props.disabled || changeType !== ChangeType.unchanged}
      selected={props.selected || changeType !== ChangeType.unchanged}
      changeType={changeType}
      resolved={Boolean(resolved)}
    />
  );
}

interface DatePickerTextFieldProps extends OutlinedTextFieldProps {
  InputProps?: Partial<OutlinedInputProps & PickersInputProps>;
  dateRedline: FieldRedline<AHDate>;
  focused: boolean;
  onTraverseIn?: () => void;
}

const DatePickerTextField: React.FC<DatePickerTextFieldProps> = (props) => {
  const { dateRedline, focused, InputProps, slotProps, onTraverseIn, ...otherProps } = props;

  return (
    <TextField
      {...otherProps}
      label={dateRedline.label}
      focused={focused}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          ...InputProps,
          inputComponent: DiffDateInputComponent,
          inputProps: {
            dateRedline,
            onTraverseIn,
          },
        },
        inputLabel: {
          ...slotProps?.inputLabel,
        },
      }}
    />
  );
};

interface DiffDateInputComponentProps extends InputBaseComponentProps {
  dateRedline: FieldRedline<AHDate>;
  onTraverseIn?: () => void;
}

const DiffDateInputComponent = forwardRef<HTMLInputElement, DiffDateInputComponentProps>(
  function DiffDateInputComponent(props, forwardedRef) {
    const { className, onTraverseIn, dateRedline, ...otherProps } = props;

    const getTextChanges = useCallback(() => {
      const collection = new TextChangeCollection();
      const previous = dateRedline.revisedEntry?.asFormattedString ?? "";
      const next = dateRedline.currentEntry ? dateRedline.currentEntry.asFormattedString : "";
      const diff = dateRedline.diffWords ? diffWords(previous, next) : diffLines(previous, next);
      return diff.map((change) => new TextChange({ diff: change, parent: collection }));
    }, [dateRedline]);

    return (
      <DiffTextContainer>
        <DiffInputContainer className={className}>
          <DiffInputSpan>
            <DiffText
              textChanges={getTextChanges()}
              allResolved={dateRedline.isResolved}
              disablePopover={true}
              onClick={onTraverseIn}
            />
          </DiffInputSpan>
        </DiffInputContainer>
        <Input {...otherProps} inputRef={forwardedRef} style={{ position: "absolute", height: 0, width: 0 }} />
      </DiffTextContainer>
    );
  },
);

export interface RedlineDateInputProps extends DatePickerProps {
  dateRedline: FieldRedline<AHDate>;
  issues?: ProposalIssue[];
  focused: boolean;
  commentThreads: CommentThread[];
  onCommentsClicked?: () => void;
  onDateRedlineChange: (newRedline: FieldRedline<AHDate>, traversalFieldOverride?: ProposalField) => void;
  onTraverseIn?: () => void;
}

const DateRedlinePicker = (props: Readonly<RedlineDateInputProps>) => {
  const {
    dateRedline,
    focused,
    disabled,
    commentThreads,
    onCommentsClicked,
    onDateRedlineChange,
    onTraverseIn,
    ...otherProps
  } = props;

  function handleChangeAccepted() {
    try {
      onDateRedlineChange(dateRedline.accept());
    } catch (error) {
      console.error("Error accepting change.", error);
      enqueueSnackbar("Error accepting change.", { variant: "error" });
    }
  }

  function handleChangeRejected() {
    try {
      onDateRedlineChange(dateRedline.reject());
    } catch (error) {
      console.error("Error rejecting change.", error);
      enqueueSnackbar("Error rejecting change.", { variant: "error" });
    }
  }

  function handleChangeEdited(newDate: AHDate | null) {
    try {
      if (!dateRedline.currentEntry && !dateRedline.revisedEntry && newDate) {
        onDateRedlineChange(dateRedline.add(newDate), dateRedline.field);
        return;
      }
      onDateRedlineChange(dateRedline.edit(newDate), dateRedline.field);
    } catch (error) {
      console.error("Error editing change.", error);
      enqueueSnackbar("Error editing change.", { variant: "error" });
    }
  }

  function handleRemoveClicked() {
    try {
      onDateRedlineChange(dateRedline.remove(), dateRedline.field);
    } catch (error) {
      console.error("Error removing change.", error);
      enqueueSnackbar("Error removing change.", { variant: "error" });
    }
  }

  function handleUndoChanges() {
    try {
      onDateRedlineChange(dateRedline.undo(), dateRedline.field);
    } catch (error) {
      console.error("Error undoing changes.", error);
      enqueueSnackbar("Error undoing changes.", { variant: "error" });
    }
  }

  const ActionButtons = (buttonProps: IconButtonProps) => {
    return (
      <>
        {!disabled && (
          <>
            {!dateRedline.isResolved && (
              <>
                <Tooltip title="Accept Change">
                  <span>
                    <ActionButton onClick={() => handleChangeAccepted()}>
                      <CheckIcon color="success" />
                    </ActionButton>
                  </span>
                </Tooltip>
                <Tooltip title="Reject Change">
                  <span>
                    <ActionButton onClick={() => handleChangeRejected()}>
                      <CloseIcon color="error" />
                    </ActionButton>
                  </span>
                </Tooltip>
              </>
            )}
            {dateRedline.isResolved && dateRedline.currentEntry && (
              <Tooltip title="Remove">
                <span>
                  <ActionButton
                    onClick={() => {
                      handleRemoveClicked();
                    }}
                  >
                    <CloseIcon />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            {dateRedline.canBeUndone && (
              <Tooltip title="Undo Changes">
                <span>
                  <ActionButton onClick={handleUndoChanges}>
                    <UndoIcon color="secondary" />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <span>
                <ActionButton
                  {...buttonProps}
                  onClick={(event) => {
                    onTraverseIn?.();
                    buttonProps.onClick?.(event);
                    onCommentsClicked?.();
                  }}
                />
              </span>
            </Tooltip>
          </>
        )}
        <CommentsButton
          commentThreads={commentThreads}
          toolTip="Comments"
          field={dateRedline.field}
          onCommentsClicked={() => {
            onCommentsClicked?.();
            onTraverseIn?.();
          }}
        />
      </>
    );
  };

  return (
    <RedlineDatePicker
      {...otherProps}
      enableAccessibleFieldDOMStructure={false} // TODO: Re-enable when date picker text input field is properly implemented
      disablePast
      disabled={disabled}
      traversable={Boolean(onTraverseIn)}
      referenceDate={
        dateRedline.currentEntry?.asMoment ?? dateRedline.revisedEntry?.asMoment ?? dateRedline.originalEntry?.asMoment
      }
      onChange={(date: PickerValue) => {
        const newDate = date?.endOf("day") ? AHDate.fromMoment(date) : null;
        handleChangeEdited(newDate);
      }}
      slots={{
        openPickerIcon: EditCalendarIcon,
        openPickerButton: ActionButtons,
        day: (dayProps) => (
          <RevisedDay
            {...dayProps}
            originalDate={dateRedline.originalEntry}
            revisedDate={dateRedline.revisedEntry}
            currentDate={dateRedline.currentEntry}
            resolved={dateRedline.isResolved}
          />
        ),
        textField: DatePickerTextField,
      }}
      slotProps={{
        openPickerIcon: {
          color: "primary",
        },
        day: {
          originalDate: dateRedline.originalEntry,
          revisedDate: dateRedline.revisedEntry,
          currentDate: dateRedline.currentEntry,
          resolved: dateRedline.isResolved,
        } as RedlinePickersDayProps,
        textField: {
          error: Boolean(props.issues?.length),
          helperText: props.issues?.map((issue) => issue.description).join("\n"),
          variant: "outlined",
          margin: "normal",
          dateRedline: dateRedline,
          focused: focused,
          slotProps: {
            inputLabel: {
              shrink:
                Boolean(dateRedline.currentEntry) ||
                Boolean(dateRedline.originalEntry) ||
                Boolean(dateRedline.revisedEntry),
            },
          },
          onFocus: () => {
            onTraverseIn?.();
            onCommentsClicked?.();
          },
          onClick: () => {
            onTraverseIn?.();
            onCommentsClicked?.();
          },
        } as DatePickerTextFieldProps,
      }}
    />
  );
};

export default DateRedlinePicker;
